import { useTranslation } from 'react-i18next';
import { cn, findTarget } from '@shared/lib';
import { TargetSentence } from '@shared/ui';
import type { ContextNote } from '../../api/sessionApi';
import type { ExerciseKind, SessionCard } from '../../model';
import { formatIpa } from '../formatIpa';

/** Пример на английском с выделенным целевым словом (и его словоформами, см. findTarget).
 *  Режем строку по найденному отрезку — сырой HTML не вставляем. */
function HighlightedSentence({ sentence, word }: { sentence: string; word: string }) {
  const found = word ? findTarget(sentence, word) : null;
  if (!found) return <>{sentence}</>;
  const [start, end] = found;
  return (
    <>
      {sentence.slice(0, start)}
      <b>{sentence.slice(start, end)}</b>
      {sentence.slice(end)}
    </>
  );
}

interface HintLineProps {
  card: SessionCard;
  exercise: ExerciseKind;
}

/** Подсказка под героем — своя формулировка карточки. Рендерится только для
 *  ввода перевода и контекста: в обратном переводе подсказка — это сама первая
 *  буква в замаскированном слове, отдельная строка там лишняя (см. SessionStage).
 *  У контекста буква тоже открыта в пропуске, поэтому если своей формулировки
 *  нет — фолбэк на «начинается на…», чтобы строка не выглядела пустой. */
export function StageHint({ card, exercise }: HintLineProps) {
  const { t } = useTranslation();
  const clue = card.hint?.trim();
  const text = clue || (exercise === 'cloze' ? t('session.hint.startsWith', { letter: card.en[0] }) : '');
  if (!text) return null;
  return <div className="st-hint-line">≈ {text}</div>;
}

interface StageRevealProps {
  card: SessionCard;
  exercise: ExerciseKind;
  good: boolean;
  /** Подсказка бэка «знаешь слово, но в другом значении». */
  offSenseHint: string | null;
  /** Засчитано переводом другого смысла: какой имелся в виду в этом предложении. */
  contextNote?: ContextNote | null;
  /** Засчитано переводом всей фразы — напоминаем, что достаточно выделенного слова. */
  wholeSentence?: boolean;
}

/**
 * Раскрытие после ответа: эталонный ответ крупно, остальные принимаемые
 * переводы рядом, затем транскрипция (для англоязычного ввода — её не было в
 * вопросе), пример и значение. У cloze пример не дублируем — он и был вопросом.
 * Прямой перевод по простому предложению показывает перевод этого предложения с
 * выделенным изучаемым переводом, а пример из контекста не показывает: он дублировал
 * вопрос и подсказку (2026-09-26). У пропуска (2026-10-07) — перевод слова рядом с ним
 * и перевод предложения: в вопросе русского не было вовсе.
 */
export function StageReveal({ card, exercise, good, offSenseHint, contextNote, wholeSentence }: StageRevealProps) {
  const { t } = useTranslation();
  const isEnglishInput = exercise === 'reverse' || exercise === 'cloze';
  const main = isEnglishInput ? card.en.toLowerCase() : card.ru.toLowerCase();
  // Только переводы смысла этого показа: «дозаправить» рядом с «праздновать завершение»
  // не к месту (2026-10-06), хотя ответ им засчитывается.
  const rest = isEnglishInput
    ? []
    : card.senseTranslations.filter((tr) => tr.toLowerCase() !== card.ru.toLowerCase());
  const ipa = isEnglishInput ? formatIpa(card.ipa) : '';
  // Вопрос был предложением (прямой перевод и выбор перевода) — после ответа его перевод.
  const bySentence = (exercise === 'input' || exercise === 'mcq') && !!card.simpleExample;
  const example = exercise === 'cloze' || bySentence ? null : card.exampleEn;
  const sentenceRu = bySentence ? card.simpleExampleRu : null;

  return (
    <div className="st-reveal">
      <div className="st-answer-line">
        {!good && <span className="st-answer-label">{t('session.feedback.correctIs')}</span>}
        <span className={cn('st-answer-main', good && 'is-good')}>{main}</span>
        {rest.length > 0 && <span className="st-answer-rest">· {rest.join(' · ')}</span>}
        {exercise === 'cloze' && card.ru && <span className="st-answer-rest">— {card.ru.toLowerCase()}</span>}
      </div>
      {contextNote && (
        <div className="st-answer-context">
          {contextNote.matched
            ? t('session.feedback.contextNote', {
                word: card.en.toLowerCase(),
                expected: contextNote.expected,
                matched: contextNote.matched,
              })
            : t('session.feedback.contextNoteShort', {
                word: card.en.toLowerCase(),
                expected: contextNote.expected,
              })}
        </div>
      )}
      {wholeSentence && (
        <div className="st-answer-context">
          {t('session.feedback.wholeSentence', { word: card.en.toLowerCase(), expected: card.ru.toLowerCase() })}
        </div>
      )}
      {ipa && <div className="st-answer-ipa">{ipa}</div>}
      {sentenceRu && (
        <div className="st-answer-example" lang="ru">
          {/* word пустой: поиск слова регуляркой с границей слова не видит кириллицу — выделяем только по отрезкам бэка. */}
          <TargetSentence sentence={sentenceRu} spans={card.simpleExampleRuSpans} word="" markClassName="st-target" />
        </div>
      )}
      {exercise === 'cloze' && card.clozeRu && (
        <div className="st-answer-example" lang="ru">
          <TargetSentence sentence={card.clozeRu} spans={card.clozeRuSpans} word="" markClassName="st-target" />
        </div>
      )}
      {example && (
        <div className="st-answer-example">
          <HighlightedSentence sentence={example} word={card.en} />
        </div>
      )}
      {card.definition && <div className="st-answer-def">{card.definition}</div>}
      {/* Редкие переводы засчитываются ответом, но списком после ответа не выводятся (владелец 2026-10-06). */}
      {offSenseHint && (
        <div className="st-answer-offsense">
          {t('session.feedback.offSense', 'Похоже, ты учишь другое значение:')}{' '}
          <b>{offSenseHint}</b>
        </div>
      )}
    </div>
  );
}
